import { Badge, Button, ButtonStyles, Icons, List, ListItem, SelectList, Tooltip } from '@silverfort/sf-components';
import { useEffect, useMemo, useState } from 'react';

const {
    JIT_SUPPORTED_PROTOCOLS,
    PROTOCOL_DISPLAY_NAMES
}: {
    JIT_SUPPORTED_PROTOCOLS: Record<string, string>;
    PROTOCOL_DISPLAY_NAMES: Record<string, string>;
} = require('@silverfort-nodelibs/common/utils/consts');

const getProtocolDisplayName = (value: string): string => {
    const key = Object.keys(JIT_SUPPORTED_PROTOCOLS).find((k) => JIT_SUPPORTED_PROTOCOLS[k] === value);
    return key && PROTOCOL_DISPLAY_NAMES[key] != null ? PROTOCOL_DISPLAY_NAMES[key] : value;
};

export const ProtocolsSelector = ({
    selectedProtocols,
    onChangeProtocols,
    selectedServices = [],
    onChangeServices,
    defaultServices = [],
    defaultProtocols = [],
    hideOptions
}: {
    selectedProtocols: string[];
    onChangeProtocols: (protocols: string[]) => void;
    selectedServices?: string[];
    onChangeServices?: (services: string[]) => void;
    defaultServices?: string[];
    defaultProtocols?: string[];
    hideOptions?: boolean;
}) => {
    const [currentServices, setCurrentServices] = useState(selectedServices);
    const [protocolsTitles, setProtocolsTitles] = useState(
        Array.from(new Set([...selectedProtocols, ...defaultProtocols]))
    );

    useEffect(() => {
        setProtocolsTitles(Array.from(new Set([...selectedProtocols, ...defaultProtocols])));
    }, [selectedProtocols, defaultProtocols]);

    const serviceOptions = useMemo(
        () =>
            Array.from(new Set([...defaultServices, ...currentServices])).map((s) => ({
                value: s,
                title: s,
                onClick: () => {
                    setCurrentServices((prev) =>
                        prev.includes(s)
                            ? prev.filter((service) => service !== s) // If exists, remove it
                            : [...prev, s]
                    );
                }
            })),
        [defaultServices, currentServices]
    );

    const onCheck = (checkedProtocol: string) => {
        const tempProtocols = [...selectedProtocols];
        const protocolIndex = tempProtocols.findIndex((p) => p === checkedProtocol);
        if (protocolIndex !== -1) {
            tempProtocols.splice(protocolIndex, 1);
        } else {
            tempProtocols.push(checkedProtocol);
        }
        setProtocolsTitles((prev) => Array.from(new Set([...prev, ...tempProtocols])));
        onChangeProtocols(tempProtocols);
    };

    const handleServicesChange = (value: string | string[] | undefined) => {
        if (onChangeServices) {
            onChangeServices(Array.isArray(value) ? value : value ? [value] : []);
        }
    };

    const addNewServiceHandler = (newValue?: string) => {
        if (newValue && onChangeServices) {
            setCurrentServices((prev) => Array.from(new Set([...prev, newValue])));
        }
    };

    return (
        <div className="w-full h-full flex flex-col">
            <div className="flex w-full justify-between px-4 h-10 items-center border-b border-sf-gray-100 gap-2">
                <div className="text-sf font-medium">Protocols</div>
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar">
                {!hideOptions && (
                    <List>
                        {protocolsTitles
                            .sort((a, b) => getProtocolDisplayName(a).localeCompare(getProtocolDisplayName(b)))
                            .map((title, index) =>
                                title === JIT_SUPPORTED_PROTOCOLS.KERBEROS ? (
                                    <div
                                        key={`protocol_${index}`}
                                        className="flex items-center justify-between w-full gap-2 pr-2">
                                        <ListItem
                                            multiSelect
                                            title={getProtocolDisplayName(title)}
                                            onCheck={(protocol) => protocol != null && onCheck(protocol)}
                                            selected={selectedProtocols.includes(title)}
                                            value={title}
                                        />
                                        {selectedProtocols.includes(JIT_SUPPORTED_PROTOCOLS.KERBEROS) &&
                                            onChangeServices && (
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <Badge
                                                        text={`${currentServices.length} services`}
                                                        className="h-6 px-2 items-center rounded border border-sf-gray-100 text-sm font-normal w-fit"
                                                    />
                                                    <Tooltip title={<div>Edit services</div>}>
                                                        <SelectList
                                                            disableSelectAll
                                                            TriggerComponent={Button}
                                                            triggerProps={{
                                                                iconLeft: Icons.Edit,
                                                                iconSize: 16,
                                                                buttonStyle: ButtonStyles.TertiarySolid,
                                                                className: 'bg-transparent min-w-0'
                                                            }}
                                                            items={serviceOptions}
                                                            selectedItems={currentServices}
                                                            onChange={(value) => handleServicesChange(value)}
                                                            multiple
                                                            searchable
                                                            addNewItemHandler={addNewServiceHandler}
                                                            menuClassName="w-64"
                                                            menuProps={{
                                                                anchorOrigin: {
                                                                    vertical: 'bottom',
                                                                    horizontal: 'right'
                                                                },
                                                                transformOrigin: {
                                                                    vertical: 'top',
                                                                    horizontal: 'right'
                                                                }
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            )}
                                    </div>
                                ) : (
                                    <ListItem
                                        key={`protocol_${index}`}
                                        multiSelect
                                        title={getProtocolDisplayName(title)}
                                        onCheck={(protocol) => protocol != null && onCheck(protocol)}
                                        selected={selectedProtocols.includes(title)}
                                        value={title}
                                    />
                                )
                            )}
                    </List>
                )}
            </div>
        </div>
    );
};
